import React, { useState, useEffect } from "react";
import styles from "./Card.module.css";
import { ReactComponent as EditIcon } from "../../assets/images/svg/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/svg/close-icon.svg";
import productPlaceholder from "../../assets/images/svg/product-placeholder-card.svg";
import { useNavigate } from "react-router-dom";

const ShoppingCard = ({
  title,
  itemCode,
  webItemId,
  parentWebItemId,
  description,
  brand,
  additionalNotes,
  image,
  qty,
  attributes,
  isVarient,
  amount,
  beforeDiscountAmount,
  currency,
  onDelete,
  onChangeQty,
  translations,
}) => {
  const [quantity, setQuantity] = useState(qty);
  const navigate = useNavigate();
  const numericAmount = Number(amount) || 0;
  const numericBeforeDiscountAmount = Number(beforeDiscountAmount) || 0;
  const hasDiscount =
    numericBeforeDiscountAmount > 0 &&
    numericAmount > 0 &&
    numericBeforeDiscountAmount > numericAmount;
  const normalizedAttributes = Array.isArray(attributes)
    ? attributes
        .map((attribute) => ({
          label:
            attribute.attribute ||
            attribute.attribute_name ||
            attribute.attribute_display_name,
          value:
            attribute.attribute_value ||
            attribute.attribute_value_name ||
            attribute.attribute_value_display_name,
        }))
        .filter((attribute) => attribute.label && attribute.value)
    : [];
  const itemDetails = [
    ...normalizedAttributes,
    ...(additionalNotes
      ? [
          {
            label: translations?.result?.Notes || "Notes",
            value: additionalNotes,
          },
        ]
      : []),
  ];
  const formatPrice = (value) => {
    const numericValue = Number(value) || 0;
    return Number.isInteger(numericValue)
      ? numericValue
      : numericValue.toFixed(2);
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      const nextQuantity = quantity - 1;
      setQuantity(nextQuantity);
      onChangeQty(nextQuantity);
    }
  };

  const handleIncrement = () => {
    const nextQuantity = quantity + 1;
    setQuantity(nextQuantity);
    onChangeQty(nextQuantity);
  };

  const handleEdit = () => {
    localStorage.setItem("myState", JSON.stringify({ qty, title }));
    navigate(
      isVarient === true
        ? `/product/${parentWebItemId}`
        : `/product/${webItemId}`,
    );
  };

  useEffect(() => {
    setQuantity(qty);
  }, [qty]);

  return (
    <div className={styles.cardBox}>
      <div className={styles.cardGrid}>
        <div className={styles.imageColumn}>
          <img
            src={image || productPlaceholder}
            alt={title || itemCode}
            className={styles.imageFit}
          />
        </div>
        <div className={styles.detailsColumn}>
          <div className={styles.itemTitle}>{title || itemCode}</div>
          {description && (
            <div className={`${styles.itemText} ${styles.descriptionText}`}>
              <p dangerouslySetInnerHTML={{ __html: description }} />
            </div>
          )}

          {brand && (
            <div className={styles.itemText}>
              <span className={styles.itemAttribute}>
                {translations?.result?.Brand || "Brand"}
              </span>
              : {brand}
            </div>
          )}

          {itemDetails.length > 0 && (
            <div className={styles.itemDetails}>
              <div className={styles.itemSubTitle}>
                {translations?.result?.ItemDetails || "Item Details"}
              </div>
              <div className={styles.dividerCart}></div>
              {itemDetails.map((attribute, index) => (
                <div
                  className={styles.itemDetailsRow}
                  key={`${attribute.label}-${index}`}
                >
                  <span className={styles.itemAttribute}>
                    {attribute.label}
                  </span>
                  <span className={styles.itemDetailsText}>
                    {attribute.value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={styles.purchaseColumn}>
          <div className={styles.price}>
            <span className={styles.currentPrice}>
              <span>{formatPrice(numericAmount)}</span>
              <span className={styles.currency}>{currency}</span>
            </span>
            {hasDiscount && (
              <span className={styles.beforeDiscountPrice}>
                <span>{formatPrice(numericBeforeDiscountAmount)}</span>
                <span className={styles.beforeDiscountCurrency}>
                  {currency}
                </span>
              </span>
            )}
          </div>
          <div className={styles.itemText}>
            {translations?.result?.PriceWithoutTAX ||
              "This price without TAX and Delivery"}
          </div>
          <div className={styles.quantityText}>
            {translations?.result?.Quantity || "Quantity"}
          </div>

          <div className={styles.quantityInputGroup}>
            <button
              type="button"
              className={`${styles.quantityButton} ${styles.quantityButtonMinus}`}
              onClick={handleDecrement}
              disabled={quantity <= 1}
              aria-label="Decrease quantity"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="2"
                viewBox="0 0 12 2"
                fill="none"
              >
                <path
                  d="M1 1H11"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <input
              type="number"
              step="1"
              max=""
              value={quantity}
              name="quantity"
              className={styles.quantityField}
              readOnly
            />

            <button
              type="button"
              className={`${styles.quantityButton} ${styles.quantityButtonPlus}`}
              onClick={handleIncrement}
              aria-label="Increase quantity"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="13"
                viewBox="0 0 12 13"
                fill="none"
              >
                <path
                  d="M6.13221 1.5556L6.11914 11.5556"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M1.11084 6.5556H11.1108"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div className={styles.actionButtons}>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.editButton}`}
          onClick={handleEdit}
          aria-label="Edit product"
        >
          <EditIcon className={styles.icon} />
        </button>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.deleteButton}`}
          onClick={() => onDelete()}
          aria-label="Remove product"
        >
          <DeleteIcon className={styles.icon} />
        </button>
      </div>
    </div>
  );
};

export default ShoppingCard;
